'use client'

import { useMemo } from 'react'
import { calculatePasswordStrength, getStrengthLabel } from '@/lib/passwordStrength'
import { Shield } from 'lucide-react'

interface PasswordStrengthMeterProps {
  password: string
  className?: string
}

export function PasswordStrengthMeter({ password, className = '' }: PasswordStrengthMeterProps) {
  const strength = useMemo(() => calculatePasswordStrength(password), [password])

  if (!password) {
    return null
  }

  // Map strength to a ring color class used for visual outline
  const ringColor = (() => {
    switch (strength.strength) {
      case 'weak':
        return 'ring-danger-red'
      case 'fair':
        return 'ring-warning-yellow'
      case 'good':
        return 'ring-info-blue'
      case 'strong':
      case 'very-strong':
        return 'ring-primary'
      default:
        return 'ring-gray-400'
    }
  })()

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1">
          <Shield className="w-3 h-3" />
          <span className="text-muted-foreground">Strength:</span>
          <span className="font-medium text-foreground">
            {getStrengthLabel(strength.strength)}
          </span>
        </div>
        <span className="text-muted-foreground">{strength.score}%</span>
      </div>
      
      {/* Progress bar + color ring */}
      <div className={`w-full bg-charcoal-700 rounded-full h-2 overflow-hidden ring-1 ring-offset-2 ring-offset-charcoal-800 ${ringColor}`}>
        <div
          className={`h-full transition-all duration-300 ease-out ${strength.color}`}
          style={{ width: `${strength.score}%` }}
        />
      </div>
      
      {/* Strength segments with subtle ring on active parts */}
      <div className="flex gap-1">
        {[20, 40, 60, 80, 100].map((threshold, index) => (
          <div
            key={threshold}
            className={`flex-1 h-1 rounded-full transition-all duration-300 ${
              strength.score >= threshold
                ? `${strength.color} ring-1 ${ringColor}`
                : 'bg-charcoal-700'
            }`}
          />
        ))}
      </div>
      
      {/* Feedback */}
      {strength.feedback.length > 0 && (
        <div className="text-xs space-y-1">
          {strength.feedback.map((tip, index) => (
            <p key={index} className="text-muted-foreground">
              • {tip}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
